/*!

=========================================================
* Paper Dashboard React - v1.3.2
=========================================================

* Product Page: https://www.creative-tim.com/product/paper-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

* Licensed under MIT (https://github.com/creativetimofficial/paper-dashboard-react/blob/main/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
import {
  Chart as ChartJS,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Filler,
  Tooltip,
  LineController,
  BarController,
} from 'chart.js';
import { Chart,Line } from 'react-chartjs-2';
// reactstrap components
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  CardTitle,
  Row,
  Col,
  Button,
} from "reactstrap";
// core components
import {
  dashboard24HoursPerformanceChart,
  dashboardEmailStatisticsChart,
  dashboardNASDAQChart,
} from "variables/charts.js";
import "../assets/css/extra.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileLines } from '@fortawesome/free-solid-svg-icons'
import zoomPlugin from 'chartjs-plugin-zoom';
import Hammer from "hammerjs";
import { AvatarGenerator } from 'random-avatar-generator';
import AddProject from "../components/AddProject";
import NotificationAlert from "react-notification-alert";


ChartJS.register(
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Filler,
  Tooltip,
  LineController,
  BarController,
  zoomPlugin
);


function Dashboard() {
  const generator = new AvatarGenerator();

  const [projectNumber, setProjectNumber] = React.useState(0);
  const [loc, setLoc] = React.useState(0);
  const [refactorings, setRefactorings] = React.useState(0);
  const [projects, setProjects] = React.useState([]);
  const [lessDebtDevelopers, setLessDebtDevelopers] = React.useState([]);
  const [moreRefactoringsDevelopers, setMoreRefactoringsDevelopers] = React.useState([]);
  const [dataChart, setDataChart] = React.useState({labels:[], datasets:[]});


  const notificationAlert = React.useRef();
  const notify = (message) => {
    var options = {
      place: "tr",
      message: (
        <div>
          <div>
            {message}
          </div>
        </div>
      ),
      type: "warning",
      icon: "nc-icon nc-bell-55",
      autoDismiss: 7,
    };
    notificationAlert.current.notificationAlert(options);
  }

  /**
   * Get general data from API
   */
  const fetchGeneralData = async () => {
    console.log("Fetching org data...");
    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + '/organization/stats?name=' + process.env.REACT_APP_ORG,
        {
          method: "GET",
          redirect: "follow",
          headers: {Authorization: `Bearer ${localStorage.getItem("accessToken")}`}
        }
      );
      const result = await response.json();
      if(response.status === 200) {
        var obj = JSON.parse(JSON.stringify(result));
        //get general stats
        setLoc(obj.totalLinesOfCode);
        setRefactorings(obj.totalRefactorings);
        setProjectNumber(obj.projectNumber);
        
        //get developers
        setLessDebtDevelopers(obj.lessDebtDevelopers);
        setMoreRefactoringsDevelopers(obj.moreRefactoringsDevelopers);

        //get chart data
        var labels = [];
        var dataAdditional = [];
        var dataCumulative = [];
        var previousCumulative = 0;
        for(var i=0; i<obj.allProjectsWeekDebt.length; i++) {
          labels.push(obj.allProjectsWeekDebt[i].date);
          dataAdditional.push(obj.allProjectsWeekDebt[i].debt);
          previousCumulative += obj.allProjectsWeekDebt[i].debt;
          dataCumulative.push(previousCumulative);
        }
        setDataChart({
          labels,
          datasets: [
            {
              type: 'bar',
              label: 'Additional Debt',
              borderWidth: 2,
              borderColor: 'rgba(251, 198, 88, 0.8)',
              backgroundColor: 'rgba(251, 198, 88, 0.7)',
              data: dataAdditional,
              yAxisID: 'y',
            },
            {
              type: 'line',
              fill: true,
              label: 'Cumulative Debt',
              borderWidth: 2,
              backgroundColor: 'rgb(17 159 80)',
              data: dataCumulative,
              yAxisID: 'y1',
            },
          ],
        });
      }
      else {
        console.log("API error:", result);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  }

  /**
   * Get projects data from API
   */
  const fetchProjectData = async () => {
    console.log("Fetching projects data...");
    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + '/organization/projects?name=' + process.env.REACT_APP_ORG,
        {
          method: "GET",
          redirect: "follow",
          headers: {Authorization: `Bearer ${localStorage.getItem("accessToken")}`}
        }
      );
      const result = await response.json();
      if(response.status === 200) {
        var obj = JSON.parse(JSON.stringify(result));
        //get each project
        setProjects(obj);
      }
      else {
        console.log("API error:", result);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  }

  /**
   * Fetch data from API when component is mounted
   */
  React.useEffect(() => {
    fetchGeneralData();
    fetchProjectData();
  }, []);

  /**
   * Reset zoom of chart
   */
  function reset(e) {
    ChartJS.getChart('chart').resetZoom();
  }

  /**
   * Add project to API
   */
  const handleClickAddProject = (url,label) => {
    console.log("url: "+url);
    fetch(process.env.REACT_APP_API_URL + '/project', {
      method: "POST",
      redirect: "follow",
      headers: {
        'Accept': 'application/json, text/plain',
        'Content-Type': 'application/json;charset=UTF-8',
        'Authorization': `Bearer ${localStorage.getItem("accessToken")}`
      },
      body: JSON.stringify({url: url, owner: process.env.REACT_APP_ORG, refactoringLabel: label}),
    })
    .then(response => response.json())
    .then(() => fetchProjectData())
  }

  function handelClickUrgent(projectName) {
    window.location.href = "/admin/urgent?name="+projectName;
  }

  function handelClickHighRist(projectName) {
    window.location.href = "/admin/highrisk?name="+projectName;
  }

  function handelClickMore(projectName) {
    window.location.href = "/admin/more?name="+projectName;
  }

  async function handelClickAnalyze(projectName) {
    console.log("projectName: "+projectName);
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + '/project/analyze?owner='+process.env.REACT_APP_ORG+'&name='+projectName, {
        method: "POST",
        redirect: "follow",
        headers: {
          'Accept': 'application/json, text/plain',
          'Content-Type': 'application/json;charset=UTF-8',
          'Authorization': `Bearer ${localStorage.getItem("accessToken")}`
        }
      });
      const result = await response.json();
      if(response.status === 200) {  
        fetchProjectData();
        fetchGeneralData();
      }
      else if(response.status === 406){
        try {
          const response = await fetch(
            process.env.REACT_APP_API_URL + '/running/debt?owner='+process.env.REACT_APP_ORG+'&name='+projectName,{
              method: "GET",
              redirect: "follow",
              headers: {Authorization: `Bearer ${localStorage.getItem("accessToken")}`}
            });
          const result = await response.json();
          if(response.status === 200) {
            var obj = JSON.parse(JSON.stringify(result));
            var now= ((obj.stepNow / obj.stepsTotal)*100).toFixed();
            var total= ((obj.stepsTotal / obj.stepsTotal)*100).toFixed();
            notify("Debt analysis is running for this project, please wait " +now+ "%");
          }
          else {
            console.log("API error:", result);
            notify("Debt analysis is running for this project, please wait...");
          }
        } catch (error) {
          console.error("Error:", error);
          notify("Debt analysis is running for this project, please wait...");
        }
      }
      else if(response.status === 409){
        notify("Debt analysis is running for another project, this project is in queue, please wait...")
      }
      else {
        console.log("API error:", result);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  }

  return (
    <>
      <div className="content">
        <NotificationAlert ref={notificationAlert} />
        <Row>
          <Col lg="3" md="4" sm="4">
            <Row>
              <Col lg="12" md="12" sm="12">
                <Card className="card-stats">
                  <CardBody style={{paddingBottom:"15px"}}>
                    <Row>
                      <Col md="4" xs="5">
                        <div className="icon-big text-center icon-warning">
                          <i className="nc-icon nc-laptop text-warning" />
                        </div>
                      </Col>
                      <Col md="8" xs="7">
                        <div className="numbers">
                          <p className="card-category">Projects</p>
                          <CardTitle tag="p">{projectNumber.toLocaleString()}</CardTitle>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
              <Col lg="12" md="12" sm="12">
                <Card className="card-stats">
                  <CardBody style={{paddingBottom:"15px"}}>
                    <Row>
                      <Col md="4" xs="5">
                        <div className="icon-big text-center icon-warning">
                          <FontAwesomeIcon icon={faFileLines} style={{color: "#119f50",}} />
                        </div>
                      </Col>
                      <Col md="8" xs="7">
                        <div className="numbers">
                          <p className="card-category">Lines of Code</p>
                          <CardTitle tag="p">{loc.toLocaleString()}</CardTitle>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
              <Col lg="12" md="12" sm="12">
                <Card className="card-stats">
                  <CardBody style={{paddingBottom:"15px"}}>
                    <Row>
                      <Col md="4" xs="5">
                        <div className="icon-big text-center icon-warning">
                          <i className="nc-icon nc-vector text-danger" />
                        </div>
                      </Col>
                      <Col md="8" xs="7">
                        <div className="numbers">
                          <p className="card-category">Refactorings</p>
                          <CardTitle tag="p">{refactorings.toLocaleString()}</CardTitle>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Col>
          <Col lg="9" md="8" sm="8">
            <Row>
              <Col md="12">
                <Card>
                  <CardHeader style={{display:"flex", justifyContent:"space-between"}}>
                    <div>
                      <CardTitle tag="h5">Cumulative Debt</CardTitle>
                      <p className="card-category">From all projects per week</p>
                    </div>
                    <div style={{marginTop:"auto"}}>
                      <button className="btn btn-secondary btn-round" style={{float:"right", marginRight:"10px"}} onClick={reset}>Reset Zoom</button>
                    </div>
                  </CardHeader>
                  <CardBody>
                    <Chart id="chart" data={dataChart} options=
                      {{maintainAspectRatio:false, 
                        plugins: {
                          zoom: {
                            zoom: {
                              wheel: {
                                enabled: true
                              },
                              mode: "x",
                              speed: 100
                            },
                            pan: {
                              enabled: true,
                              mode: "xy",
                              speed: 0.5
                            }
                          }
                        },
                        scales: {
                          y: {
                            type: 'linear',
                            display: true,
                            position: 'left',
                          },
                          y1: {
                            type: 'linear',
                            display: true,
                            position: 'right',
                            grid: {
                              drawOnChartArea: false,
                            },
                          },
                        }
                      }} width={400} height={300} />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h5">Projects</CardTitle>
              </CardHeader>
              <CardBody>
                {projects.map((project) => (
                  <Row>
                    <Card style={{backgroundColor:"#a0a0a026", width:"100%", marginLeft:"5px", marginRight:"5px"}}>
                      <CardBody>
                        <Row style={{minHeight:"70px"}}>
                          <Col md="3" xs="5" style={{margin:"auto"}}>
                            <div style={{fontWeight: "bold"}}>{project.name}</div>
                            <div style={{fontStyle: "italic"}}>{project.linesOfCode != null ? <>Loc: {project.linesOfCode.toLocaleString()}</> : <>Loc: 0</>}</div>
                          </Col>
                          <Col md="2" xs="5" style={{margin:"auto", textAlign:"center"}}>
                            <div>Cumulative Debt</div>
                            <div>{project.cumulativeDebt != null ? project.cumulativeDebt.toLocaleString(undefined, {maximumFractionDigits:2}) : 0} €</div>
                          </Col>
                          <Col md="2" xs="5" style={{margin:"auto", textAlign:"center"}}>
                            <div>Week's Debt</div>
                            <div>{project.weekDebt.length>0 ? project.weekDebt[project.weekDebt.length - 1].debt.toLocaleString(undefined, {maximumFractionDigits:2}) : 0} €</div>
                          </Col>
                          <Col md="2" xs="5" style={{margin:"auto", textAlign:"center"}}>
                            <div>Interest Rate</div>
                            <div>{project.interestRateAvg != null ? (project.interestRateAvg * 100).toLocaleString(undefined, {maximumFractionDigits:2}) : 0} %</div>
                          </Col>
                          <Col md="3" xs="5" style={{margin:"auto", textAlign:"right"}}>
                              <Button
                                className="btn-round"
                                color="primary"
                                type="submit"
                                style={{margin:"4px", padding:"4px", backgroundColor: "transparent"}}
                                title="More"
                                onClick={()=>handelClickMore(project.name)}>
                                <img src={require("assets/img/more.png")} style={{width:"35px"}}/>
                              </Button>
                              <Button 
                                className="btn-round"
                                color="primary"
                                type="submit"
                                style={{margin:"4px", padding:"4px", backgroundColor: "transparent"}}
                                title="High TD Risk"
                                onClick={()=>handelClickHighRist(project.name)}>
                                <img src={require("assets/img/deepincode.png")} style={{width:"60px"}}/>
                              </Button>
                              <Button
                                className="btn-round"
                                color="primary"
                                type="submit"
                                style={{margin:"4px", padding:"4px", backgroundColor: "transparent"}}
                                title="Refactoring Urgency"
                                onClick={()=>handelClickUrgent(project.name)}>
                                <img src={require("assets/img/codewise.png")} style={{width:"60px"}}/>
                              </Button>
                              {/* <Button
                                className="btn-round"
                                color="primary"
                                type="submit"
                                style={{margin:"4px", padding:"8px"}}>
                                Sites
                              </Button> */}
                              <Button
                                className="btn-round"
                                color="primary"
                                type="submit"
                                style={{margin:"4px", padding:"4px", backgroundColor: "transparent"}}
                                title="Analyze"
                                onClick={()=>handelClickAnalyze(project.name)}>
                                <img src={require("assets/img/analyze.png")} style={{width:"40px"}}/>
                              </Button>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Row>
                ))}
              </CardBody>
              <CardFooter style={{textAlign:"center"}}>
                  <AddProject onClickAddProject={handleClickAddProject}/>
              </CardFooter>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col md="6">
            <Card>
              <CardHeader>
                <CardTitle tag="h5">Cleaner Contributors</CardTitle>
              </CardHeader>
              <CardBody>
                {lessDebtDevelopers.map((developer, index) => (
                  <Row style={{marginBottom: "5px"}}>
                    <Col md="1" xs="1" style={{margin:"auto", padding:"4px"}}>
                      <div style={{textAlign:"center"}}>{index+1}.</div>
                    </Col>
                    <Col md="3" xs="3" style={{margin:"auto"}}>
                      <img src={developer.avatarUrl!=null ? developer.avatarUrl : generator.generateRandomAvatar(developer.name)} style={{borderRadius: "50%", width:"70px"}}/>
                    </Col>
                    <Col md="4" xs="3" style={{margin:"auto"}}>
                      <div>{developer.name}</div>
                    </Col>
                    <Col md="4" xs="3" style={{margin:"auto", textAlign:"center"}}>
                      <div>Debt per Line:</div>
                      <div> {(developer.debtAddition / developer.linesOfCodeAddition).toFixed(2)==0.00 ? (developer.debtAddition / developer.linesOfCodeAddition).toFixed(4) : (developer.debtAddition / developer.linesOfCodeAddition).toFixed(2)} €</div>
                    </Col>
                  </Row>
                ))}
              </CardBody>
            </Card>
          </Col>
          <Col md="6">
            <Card>
              <CardHeader>
                <CardTitle tag="h5">Most Refactoring Contributors</CardTitle>
              </CardHeader>
              <CardBody>
                {moreRefactoringsDevelopers.map((developer, index) => (
                  <Row style={{marginBottom: "5px"}}>
                    <Col md="1" xs="1" style={{margin:"auto", padding:"4px"}}>
                      <div style={{textAlign:"center"}}>{index+1}.</div>
                    </Col>
                    <Col md="3" xs="3" style={{margin:"auto"}}>
                      <img src={developer.avatarUrl!=null ? developer.avatarUrl : generator.generateRandomAvatar(developer.name)} style={{borderRadius: "50%", width:"70px"}}/>
                    </Col>
                    <Col md="4" xs="3" style={{margin:"auto"}}>
                      <div>{developer.name}</div>
                    </Col>
                    <Col md="4" xs="3" style={{margin:"auto", textAlign:"center"}}>
                      <div>Refactorings</div>
                      <div>{developer.numberOfRefactorings}</div>
                    </Col>
                  </Row>
                ))}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default Dashboard;
